import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../data/siteMap';
import LegacyPhotoGallery from '../components/LegacyPhotoGallery';

const milestones = [
  {
    year: '1985',
    era: 'Foundation',
    title: 'Okigwe Medical Centre Annex',
    description:
      'Founded following conversations with Mr. Ohoeree Mgbeokere (Ochendo) and Dr. C. C. Onwuka — the first registered hospital in what is now Umunneochi LGA, Abia State.',
    note: 'Established alongside the late Dr. C. C. Onwuka, then Medical Director of Okigwe Medical Centre, who shared a passion for community-centered care.',
  },
  {
    year: '1991',
    era: 'Independence',
    title: 'The Great Emmanuel Hospital and Maternity',
    description:
      'With the creation of Abia State and administrative separation of Isuochi from Okigwe, the facility evolved through upgrades and regulatory alignment to operate independently.',
  },
  {
    year: 'Today',
    era: 'Transformation',
    title: 'The Great Emmanuel Hospital Limited',
    description:
      'Now a division of PureMedify Limited — strengthened for evidence-based care, strategic partnerships, and sustainable healthcare delivery.',
  },
];

const impactStats = [
  { value: '30,000+', label: 'Patients cared for since inception' },
  { value: '20+', label: 'Medical doctors who have served' },
  { value: '150', label: 'Nurses dedicated to patient care' },
  { value: '40+ yrs', label: 'Philanthropic healthcare commitment' },
];

const physicians = [
  'Dr. Onwuka',
  'Dr. Chukwuneke',
  'Dr. Nwambara',
  'Dr. Ifo',
  'Dr. Osigwe',
  'Dr. Uwaezuoke',
  'Dr. Uzu',
  'Dr. Abba',
  'Dr. Mgbeke',
];

const GreatEmmanuelHospital = () => (
  <div className="flex min-h-screen w-full flex-col overflow-x-hidden bg-gradient-to-br from-blue-50 to-teal-100">
    <Helmet>
      <title>The Great Emmanuel Hospital Limited | PUREMEDIFY</title>
      <meta
        name="description"
        content="From loss to legacy — The Great Emmanuel Hospital Limited delivers compassionate, evidence-based healthcare in Umunneochi, Abia State. Service to God and Humanity."
      />
      <link rel="canonical" href={`${SITE_URL}/great-emmanuel-hospital`} />
    </Helmet>

    {/* Hero */}
    <section className="relative z-0 isolate flex min-h-[520px] w-full flex-col items-center justify-center overflow-hidden sm:min-h-[580px] md:min-h-[680px]">
      <img
        src={process.env.PUBLIC_URL + '/image/great-emmanuel-hospital-1980s.jpg'}
        alt="A 1980s community hospital in Nigeria — the era when The Great Emmanuel Hospital was founded"
        className="absolute inset-0 z-0 h-full w-full object-cover object-center"
      />
      <div className="absolute inset-0 z-10 bg-[#0a2463]/70" />
      <div className="absolute inset-0 z-20 bg-gradient-to-br from-lochmara-blue/80 via-[#004487]/65 to-red-900/25" />

      <div className="relative z-30 mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 sm:py-16 md:py-20">
        <div className="flex flex-col items-center gap-8 md:flex-row md:items-center md:gap-10 lg:gap-14">
          {/* Logo */}
          <div className="flex shrink-0 flex-col items-center">
            <div className="h-36 w-36 shrink-0 overflow-hidden rounded-full shadow-2xl ring-4 ring-white/30 sm:h-44 sm:w-44 md:h-48 md:w-48 lg:h-52 lg:w-52">
              <img
                src={process.env.PUBLIC_URL + '/image/great-emmanuel-hospital-logo.png'}
                alt="The Great Emmanuel Hospital Limited logo"
                className="h-full w-full object-contain bg-transparent"
              />
            </div>
            <p className="mt-4 max-w-[12rem] text-center font-redhat text-xs font-medium uppercase tracking-widest text-white/70 sm:text-sm">
              Est. community healthcare legacy
            </p>
          </div>

          {/* Copy */}
          <div className="flex-1 text-center md:text-left">
            <h1 className="mb-3 font-ubuntu text-2xl font-bold leading-tight text-white drop-shadow-lg sm:text-3xl md:text-4xl lg:text-5xl">
              The Great Emmanuel Hospital Limited
            </h1>

            <span className="mb-4 inline-block rounded-full border border-white/30 bg-white/10 px-4 py-1.5 font-redhat text-xs font-semibold uppercase tracking-wide text-white/90 backdrop-blur-sm sm:text-sm">
              A Division of PureMedify Limited
            </span>

            <p className="mb-1 font-ubuntu text-xl font-bold text-white sm:text-2xl md:text-3xl">
              From Loss to Legacy
            </p>
            <p className="mb-4 font-ubuntu text-base font-semibold text-malachite-green sm:text-lg md:text-xl">
              A Vision That Heals Generations
            </p>

            <p className="mb-6 font-redhat text-sm text-white/85 sm:text-base">
              By Elder David I. Mgbeke
            </p>

            <div className="mx-auto max-w-xl rounded-2xl border border-white/20 bg-white/10 p-4 backdrop-blur-md md:mx-0 md:max-w-lg md:p-5">
              <p className="mb-2 font-redhat text-xs font-semibold uppercase tracking-wider text-white/80 sm:text-sm">
                Motto: Service to God and Humanity
              </p>
              <blockquote className="border-l-4 border-red-500 pl-4 font-ubuntu text-base font-bold italic leading-snug text-white sm:text-lg md:text-xl">
                &ldquo;We dress the wound, God heals it.&rdquo;
              </blockquote>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Introduction */}
    <section className="relative w-full overflow-hidden px-4 py-12 sm:py-16 md:py-20">
      <div className="pointer-events-none absolute left-0 top-10 h-48 w-48 rounded-full bg-lochmara-blue/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-56 w-56 rounded-full bg-malachite-green/10 blur-3xl" />

      <div className="relative mx-auto max-w-5xl">
        <div className="mb-10 text-center">
          <span className="mb-3 inline-block rounded-full bg-lochmara-blue/10 px-3 py-1 font-redhat text-xs font-semibold uppercase tracking-wide text-lochmara-blue sm:px-4 sm:text-sm sm:tracking-widest">
            Introduction
          </span>
          <h2 className="px-2 font-ubuntu text-2xl font-bold text-lochmara-blue sm:text-3xl md:text-4xl">
            Where It All Began
          </h2>
        </div>

        <article className="relative overflow-hidden rounded-2xl border border-white/80 bg-white/90 shadow-xl backdrop-blur-sm sm:rounded-3xl">
          <div className="absolute left-0 top-0 h-1.5 w-full bg-gradient-to-r from-lochmara-blue via-brand to-malachite-green" />
          <div className="grid grid-cols-1 gap-0 md:grid-cols-5">
            <div className="flex items-center justify-center bg-gradient-to-br from-lochmara-blue/5 to-brand/5 p-6 sm:p-8 md:col-span-2 md:p-10">
              <div className="text-center md:text-left">
                <span className="font-ubuntu text-5xl leading-none text-lochmara-blue/20 sm:text-7xl md:text-8xl">&ldquo;</span>
                <p className="-mt-4 font-ubuntu text-lg font-bold leading-snug text-congress-blue sm:-mt-6 sm:text-xl md:text-2xl">
                  Loss became the catalyst for a lifelong commitment to healing.
                </p>
              </div>
            </div>
            <div className="flex flex-col justify-center p-6 sm:p-8 md:col-span-3 md:p-10">
              <p className="font-redhat text-base leading-relaxed text-gray-700 sm:text-lg md:text-xl">
                A single childhood loss became the catalyst for a lifelong commitment to healing. The Great Emmanuel
                Hospital Limited stands today as a testament to how compassion, vision, and resilience can transform
                personal tragedy into a lasting institution of hope, delivering quality healthcare, dignity, and
                life-saving services to thousands across generations.
              </p>
            </div>
          </div>
        </article>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[
            { label: 'Compassion', desc: 'Care rooted in empathy and human dignity', dot: 'bg-lochmara-blue' },
            { label: 'Vision', desc: 'A purpose that outlives personal tragedy', dot: 'bg-brand' },
            { label: 'Resilience', desc: 'Four decades of unwavering service', dot: 'bg-malachite-green' },
          ].map((pillar) => (
            <div
              key={pillar.label}
              className="rounded-2xl border border-white/80 bg-white/80 p-5 text-center shadow-md backdrop-blur-sm transition hover:-translate-y-1 hover:shadow-lg"
            >
              <span className={`mb-2 inline-block h-2 w-2 rounded-full ${pillar.dot}`} />
              <h3 className="font-ubuntu text-lg font-bold text-congress-blue">{pillar.label}</h3>
              <p className="mt-1 font-redhat text-sm text-gray-600">{pillar.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Our Story */}
    <section className="relative w-full overflow-hidden bg-gradient-to-b from-white/70 via-blue-50/50 to-transparent px-4 py-12 sm:py-16 md:py-20">
      <div className="relative mx-auto max-w-6xl">
        <div className="mb-8 text-center sm:mb-12">
          <span className="mb-3 inline-block rounded-full bg-malachite-green/10 px-3 py-1 font-redhat text-xs font-semibold uppercase tracking-wide text-malachite-green sm:px-4 sm:text-sm sm:tracking-widest">
            Our Story
          </span>
          <h2 className="px-2 font-ubuntu text-2xl font-bold text-lochmara-blue sm:text-3xl md:text-4xl">
            From Personal Loss to Public Legacy
          </h2>
          <p className="mx-auto mt-3 max-w-2xl px-2 font-redhat text-sm text-gray-600 sm:text-base md:text-lg">
            The founding vision of Elder David I. Mgbeke — shaped by grief, clarified by knowledge, sustained by faith.
          </p>
        </div>

        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-12 lg:gap-12">
          {/* Narrative spine */}
          <div className="relative order-2 lg:order-1 lg:col-span-7">
            <div className="absolute bottom-4 left-4 top-4 w-0.5 rounded-full bg-gradient-to-b from-lochmara-blue via-brand to-malachite-green sm:left-5" />

            <div className="space-y-6 sm:space-y-8">
              {[
                {
                  chapter: 'Chapter I',
                  title: 'The Loss',
                  text: 'The vision for The Great Emmanuel Hospital was born out of profound personal loss. As a child, Elder David I. Mgbeke witnessed the devastating consequences of malnutrition when her younger sister, Umeh Ihealahurunwa, passed away in his arms. At the time, neither his mother nor he fully understood what was happening.',
                  accent: 'lochmara-blue',
                },
                {
                  chapter: 'Chapter II',
                  title: 'The Signs Unread',
                  text: 'Her body, once bloated, began to lose weight — a change they mistakenly believed signaled recovery. She developed an unusual craving for sand and clay, and within days, she was gone.',
                  accent: 'brand',
                },
                {
                  chapter: 'Chapter III',
                  title: 'Painful Clarity',
                  text: 'Years later, through coaching by the late Dr Onyeaso and deep study of nutrition, came a painful clarity: the death was preventable — a reflection of systemic failures within Nigeria\'s public health infrastructure.',
                  accent: 'congress-blue',
                  highlight: true,
                },
                {
                  chapter: 'Chapter IV',
                  title: 'A Covenant of Care',
                  text: 'Determined to reduce the burden of such preventable losses within the community, the resolve was to invest in healthcare delivery. From the outset, this vision was anchored in service to God and Humanity. Ten percent of the hospital\'s profits were deliberately allocated to philanthropic healthcare for indigent patients — a commitment sustained for over 40 years.',
                  accent: 'malachite-green',
                },
              ].map((beat) => (
                <div key={beat.chapter} className="relative pl-12 sm:pl-14 md:pl-16">
                  <div
                    className={`absolute left-0 top-5 flex h-8 w-8 items-center justify-center rounded-full border-4 border-white shadow-md sm:top-6 sm:h-9 sm:w-9 md:h-10 md:w-10 ${
                      beat.accent === 'malachite-green'
                        ? 'bg-malachite-green'
                        : beat.accent === 'brand'
                          ? 'bg-brand'
                          : beat.accent === 'congress-blue'
                            ? 'bg-congress-blue'
                            : 'bg-lochmara-blue'
                    }`}
                  >
                    <span className="h-2 w-2 rounded-full bg-white" />
                  </div>

                  <article
                    className={`rounded-2xl border bg-white/90 p-5 shadow-lg backdrop-blur-sm transition hover:-translate-y-0.5 hover:shadow-xl sm:p-6 md:p-7 ${
                      beat.highlight
                        ? 'border-malachite-green/30 ring-1 ring-malachite-green/20'
                        : 'border-white/80'
                    }`}
                  >
                    <span className="mb-1 block font-redhat text-xs font-bold uppercase tracking-wide text-gray-400 sm:tracking-widest">
                      {beat.chapter}
                    </span>
                    <h3 className="mb-2 font-ubuntu text-lg font-bold text-congress-blue sm:mb-3 sm:text-xl md:text-2xl">
                      {beat.title}
                    </h3>
                    <p
                      className={`font-redhat text-sm leading-relaxed sm:text-base md:text-lg ${
                        beat.highlight ? 'italic text-congress-blue' : 'text-gray-700'
                      }`}
                    >
                      {beat.text}
                    </p>
                  </article>
                </div>
              ))}
            </div>
          </div>

          {/* Founder portrait panel — shown first on mobile */}
          <div className="order-1 lg:order-2 lg:col-span-5 lg:sticky lg:top-24">
            <div className="overflow-hidden rounded-2xl border border-white/80 bg-white/90 shadow-xl backdrop-blur-sm sm:rounded-3xl">
              <div className="relative">
                <img
                  src={process.env.PUBLIC_URL + '/image/david.jpeg'}
                  alt="Elder David I. Mgbeke"
                  className="h-64 w-full object-cover object-top sm:h-72 md:h-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-congress-blue/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white sm:p-6">
                  <p className="font-redhat text-xs uppercase tracking-wide text-white/80 sm:text-sm sm:tracking-widest">Founder</p>
                  <h3 className="font-ubuntu text-xl font-bold sm:text-2xl">Elder David I. Mgbeke</h3>
                </div>
              </div>
              <div className="space-y-3 p-4 sm:space-y-4 sm:p-6">
                <div className="flex items-center gap-3 rounded-xl bg-lochmara-blue/5 px-3 py-3 sm:px-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-lochmara-blue font-ubuntu text-xs font-bold text-white sm:h-10 sm:w-10 sm:text-sm">
                    10%
                  </span>
                  <p className="font-redhat text-xs text-gray-700 sm:text-sm">
                    Of hospital profits allocated to philanthropic care for indigent patients
                  </p>
                </div>
                <div className="flex items-center gap-3 rounded-xl bg-malachite-green/5 px-3 py-3 sm:px-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-malachite-green font-ubuntu text-[10px] font-bold text-white sm:h-10 sm:w-10 sm:text-xs">
                    40+
                  </span>
                  <p className="font-redhat text-xs text-gray-700 sm:text-sm">
                    Years of sustained commitment to service to God and Humanity
                  </p>
                </div>
                <p className="border-t border-gray-100 pt-3 text-center font-redhat text-xs italic text-gray-500 sm:pt-4 sm:text-sm">
                  &ldquo;Service to God and Humanity&rdquo;
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <LegacyPhotoGallery />

    {/* Evolution Timeline */}
    <section className="relative w-full overflow-hidden bg-gradient-to-b from-white/80 via-blue-50/60 to-teal-50/40 px-4 py-12 sm:py-16 md:py-20">
      <div className="pointer-events-none absolute left-1/2 top-24 hidden h-64 w-64 -translate-x-1/2 rounded-full bg-lochmara-blue/5 blur-3xl md:block" />
      <div className="pointer-events-none absolute bottom-10 right-10 h-40 w-40 rounded-full bg-malachite-green/10 blur-3xl" />

      <div className="relative mx-auto max-w-5xl">
        <div className="mb-12 text-center">
          <span className="mb-3 inline-block rounded-full bg-lochmara-blue/10 px-3 py-1 font-redhat text-xs font-semibold uppercase tracking-wide text-lochmara-blue sm:px-4 sm:text-sm sm:tracking-widest">
            Our Evolution
          </span>
          <h2 className="px-2 font-ubuntu text-2xl font-bold text-lochmara-blue sm:text-3xl md:text-4xl">
            The Journey of Growth and Transformation
          </h2>
          <p className="mx-auto mt-3 max-w-2xl px-2 font-redhat text-sm text-gray-600 sm:text-base md:text-lg">
            From a community annex to an independent institution — and onward as part of PureMedify.
          </p>
        </div>

        <div className="relative">
          {/* Vertical evolution spine */}
          <div className="absolute bottom-8 left-4 top-8 w-0.5 rounded-full bg-gradient-to-b from-lochmara-blue via-brand to-malachite-green sm:left-5 md:left-1/2 md:-ml-0.5" />

          <div className="space-y-8 sm:space-y-12 md:space-y-16">
            {milestones.map((item, index) => {
              const isEven = index % 2 === 0;
              const isLast = index === milestones.length - 1;

              return (
                <div key={item.year} className="relative md:grid md:grid-cols-2 md:items-center md:gap-10">
                  {/* Timeline node */}
                  <div className="absolute left-4 top-6 z-10 flex -translate-x-1/2 flex-col items-center sm:left-5 sm:top-8 md:left-1/2">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full border-4 border-white shadow-lg sm:h-12 sm:w-12 ${
                        isLast
                          ? 'bg-gradient-to-br from-malachite-green to-brand'
                          : 'bg-gradient-to-br from-lochmara-blue to-brand'
                      }`}
                    >
                      <span className="font-ubuntu text-[9px] font-bold leading-none text-white sm:text-[10px] md:text-xs">
                        {item.year === 'Today' ? 'Now' : item.year.slice(2)}
                      </span>
                    </div>
                    {!isLast && (
                      <div className="mt-1 hidden font-redhat text-[10px] font-bold uppercase tracking-wider text-brand md:block">
                        ↓
                      </div>
                    )}
                  </div>

                  {/* Card — alternates sides on desktop */}
                  <div
                    className={`ml-11 sm:ml-14 md:ml-0 ${
                      isEven ? 'md:col-start-1 md:pr-10 md:text-right' : 'md:col-start-2 md:pl-10 md:text-left'
                    }`}
                  >
                    <article
                      className={`group relative rounded-2xl border border-white/80 bg-white/90 p-5 shadow-lg backdrop-blur-sm transition hover:-translate-y-1 hover:shadow-xl sm:p-6 md:p-8 ${
                        isEven ? 'md:mr-6' : 'md:ml-6'
                      }`}
                    >
                      {/* Era badge */}
                      <span
                        className={`mb-2 inline-block rounded-full px-2.5 py-1 font-redhat text-[10px] font-bold uppercase tracking-wide sm:mb-3 sm:px-3 sm:text-xs ${
                          index === 0
                            ? 'bg-lochmara-blue/10 text-lochmara-blue'
                            : index === 1
                              ? 'bg-brand/10 text-brand'
                              : 'bg-malachite-green/10 text-malachite-green'
                        }`}
                      >
                        {item.era}
                      </span>

                      <p className="mb-1 font-ubuntu text-xl font-bold text-congress-blue sm:text-2xl">{item.year}</p>
                      <h3 className="mb-2 break-words font-ubuntu text-lg font-bold text-lochmara-blue sm:mb-3 sm:text-xl md:text-2xl">
                        {item.title}
                      </h3>
                      <p className="font-redhat text-sm leading-relaxed text-gray-700 sm:text-base md:text-lg">
                        {item.description}
                      </p>
                      {item.note && (
                        <p className="mt-3 border-t border-gray-100 pt-3 font-redhat text-sm italic text-gray-500">
                          {item.note}
                        </p>
                      )}

                      {/* Connector arrow toward spine on desktop */}
                      <span
                        className={`absolute top-10 hidden h-0.5 w-6 bg-gradient-to-r from-lochmara-blue/40 to-brand/40 md:block ${
                          isEven ? '-right-6 from-lochmara-blue/40 to-brand/40' : '-left-6 from-brand/40 to-lochmara-blue/40'
                        }`}
                      />
                    </article>
                  </div>

                  {/* Empty column for alternating layout */}
                  <div className={`hidden md:block ${isEven ? 'md:col-start-2' : 'md:col-start-1 md:row-start-1'}`} />
                </div>
              );
            })}
          </div>

          {/* Evolution arc labels — mobile */}
          <div className="mt-8 grid grid-cols-3 gap-2 px-2 md:hidden">
            {['Community Roots', 'Independent Care', 'Partnership & Scale'].map((label, i) => (
              <div key={label} className="flex flex-col items-center gap-1.5 text-center">
                <div
                  className={`h-2.5 w-2.5 rounded-full ${
                    i === 0 ? 'bg-lochmara-blue' : i === 1 ? 'bg-brand' : 'bg-malachite-green'
                  }`}
                />
                <span className="font-redhat text-[10px] font-medium leading-tight text-gray-500">{label}</span>
              </div>
            ))}
          </div>

          {/* Evolution arc labels — desktop */}
          <div className="mt-12 hidden items-center justify-between px-4 md:flex">
            {['Community Roots', 'Independent Care', 'Partnership & Scale'].map((label, i) => (
              <div key={label} className="flex flex-col items-center gap-2">
                <div
                  className={`h-3 w-3 rounded-full ${
                    i === 0 ? 'bg-lochmara-blue' : i === 1 ? 'bg-brand' : 'bg-malachite-green'
                  }`}
                />
                <span className="font-redhat text-xs font-medium text-gray-500">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="mx-auto mt-8 max-w-3xl rounded-2xl border border-blue-100 bg-white/70 px-4 py-4 text-center font-redhat text-sm leading-relaxed text-gray-600 shadow-sm sm:mt-12 sm:px-6 sm:py-5 sm:text-base md:text-lg">
          As medicine evolved toward evidence-based practice, the institution realigned to strengthen governance,
          foster innovation, and enable partnerships with private organizations, government agencies, and NGOs —
          positioning the hospital to deliver sustainable, high-quality healthcare to the community.
        </p>
      </div>
    </section>

    {/* Impact Stats */}
    <section className="w-full bg-gradient-to-r from-lochmara-blue to-brand px-4 py-12 text-white sm:py-14">
      <div className="mx-auto max-w-5xl">
        <h2 className="mb-8 text-center font-ubuntu text-2xl font-bold sm:mb-10 sm:text-3xl md:text-4xl">Impact and Service</h2>
        <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
          {impactStats.map((stat) => (
            <div
              key={stat.label}
              className="rounded-2xl bg-white/10 p-4 text-center backdrop-blur-sm ring-1 ring-white/20 sm:p-6"
            >
              <p className="mb-1 font-ubuntu text-2xl font-bold sm:mb-2 sm:text-3xl md:text-4xl">{stat.value}</p>
              <p className="font-redhat text-xs text-white/90 sm:text-sm md:text-base">{stat.label}</p>
            </div>
          ))}
        </div>
        <p className="mx-auto mt-8 max-w-3xl text-center font-redhat text-sm leading-relaxed text-white/90 sm:mt-10 sm:text-base md:text-lg">
          From its inception to the present day, The Great Emmanuel Hospital has provided care supported by
          dedicated physicians and nurses. Each patient encounter reflects our enduring commitment to quality,
          ethics, and equity — values that have guided the institution for decades.
        </p>
      </div>
    </section>

    {/* Physicians */}
    <section className="relative w-full overflow-hidden bg-gradient-to-b from-transparent via-white/60 to-blue-50/40 px-4 py-12 sm:py-16 md:py-20">
      <div className="pointer-events-none absolute right-0 top-20 h-52 w-52 rounded-full bg-lochmara-blue/5 blur-3xl" />
      <div className="pointer-events-none absolute bottom-10 left-10 h-40 w-40 rounded-full bg-malachite-green/10 blur-3xl" />

      <div className="relative mx-auto max-w-6xl">
        <div className="mb-12 text-center">
          <span className="mb-3 inline-block rounded-full bg-lochmara-blue/10 px-3 py-1 font-redhat text-xs font-semibold uppercase tracking-wide text-lochmara-blue sm:px-4 sm:text-sm sm:tracking-widest">
            Medical Legacy
          </span>
          <h2 className="px-2 font-ubuntu text-2xl font-bold text-lochmara-blue sm:text-3xl md:text-4xl">
            Distinguished Physicians Who Have Served
          </h2>
          <p className="mx-auto mt-3 max-w-2xl px-2 font-redhat text-sm text-gray-600 sm:text-base md:text-lg">
            Decades of expertise, compassion, and commitment from physicians who shaped our institution.
          </p>
        </div>

        <div className="overflow-hidden rounded-2xl border border-white/80 bg-white/90 shadow-xl backdrop-blur-sm sm:rounded-3xl">
          <div className="bg-gradient-to-r from-lochmara-blue to-brand px-4 py-4 text-center text-white sm:px-6 sm:py-5 md:px-10">
            <p className="font-ubuntu text-2xl font-bold sm:text-3xl md:text-4xl">20+</p>
            <p className="font-redhat text-xs text-white/90 sm:text-sm md:text-base">
              Medical doctors who have contributed their expertise across generations of care
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 sm:gap-4 sm:p-6 lg:grid-cols-3 md:p-8">
            {physicians.map((name, index) => (
              <article
                key={name}
                className="group flex min-w-0 items-center gap-3 rounded-2xl border border-gray-100 bg-gradient-to-br from-blue-50/50 to-white p-3 shadow-sm transition hover:-translate-y-1 hover:border-lochmara-blue/20 hover:shadow-md sm:gap-4 sm:p-4"
              >
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-ubuntu text-sm font-bold text-white shadow-md sm:h-12 sm:w-12 ${
                    index % 3 === 0
                      ? 'bg-lochmara-blue'
                      : index % 3 === 1
                        ? 'bg-brand'
                        : 'bg-malachite-green'
                  }`}
                >
                  {name.replace('Dr. ', '').charAt(0)}
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate font-ubuntu text-sm font-bold text-congress-blue sm:text-base md:text-lg">{name}</h3>
                  <p className="font-redhat text-xs text-gray-500 sm:text-sm">Contributing Physician</p>
                </div>
                <svg
                  className="hidden h-5 w-5 shrink-0 text-lochmara-blue/30 transition group-hover:text-lochmara-blue sm:block"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </article>
            ))}

            <article className="flex items-center justify-center rounded-2xl border-2 border-dashed border-malachite-green/30 bg-malachite-green/5 p-4 text-center sm:col-span-2 sm:p-6 lg:col-span-3">
              <div>
                <span className="mb-2 inline-block rounded-full bg-malachite-green/10 px-3 py-1 font-redhat text-xs font-bold uppercase tracking-wide text-malachite-green">
                  And Many Others
                </span>
                <p className="font-redhat text-sm text-gray-600 md:text-base">
                  Alongside dedicated nurses and healthcare workers whose names may go unrecorded, but whose
                  service has been invaluable to every patient we have served.
                </p>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>

    {/* Conclusion */}
    <section className="relative w-full overflow-hidden px-4 py-12 sm:py-16 md:py-20">
      <div className="pointer-events-none absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 rounded-full bg-teal-100/60 blur-3xl" />

      <div className="relative mx-auto max-w-5xl">
        <div className="mb-10 text-center">
          <span className="mb-3 inline-block rounded-full bg-congress-blue/10 px-3 py-1 font-redhat text-xs font-semibold uppercase tracking-wide text-congress-blue sm:px-4 sm:text-sm sm:tracking-widest">
            Conclusion
          </span>
          <h2 className="px-2 font-ubuntu text-2xl font-bold text-lochmara-blue sm:text-3xl md:text-4xl">
            A Legacy That Endures
          </h2>
        </div>

        <article className="relative overflow-hidden rounded-2xl border border-white/80 bg-white/90 shadow-xl backdrop-blur-sm sm:rounded-3xl">
          <div className="absolute left-0 top-0 h-1.5 w-full bg-gradient-to-r from-congress-blue via-lochmara-blue to-malachite-green" />

          <div className="bg-gradient-to-br from-congress-blue/5 via-transparent to-malachite-green/5 px-4 py-6 text-center sm:px-6 sm:py-8 md:px-10 md:py-10">
            <span className="font-ubuntu text-5xl leading-none text-lochmara-blue/20 sm:text-6xl md:text-7xl">&ldquo;</span>
            <p className="-mt-3 font-ubuntu text-lg font-bold leading-snug text-congress-blue sm:-mt-4 sm:text-xl md:text-2xl">
              More than a healthcare facility — a legacy forged from loss, sustained by service.
            </p>
          </div>

          <div className="space-y-4 p-4 sm:space-y-6 sm:p-6 md:p-10">
            {[
              {
                step: '01',
                title: 'A Lasting Institution',
                text: 'The Great Emmanuel Hospital Limited is more than a healthcare facility; it is a legacy forged from loss, sustained by service, and driven by an unwavering belief that access to quality healthcare should never be a privilege reserved for the few.',
                accent: 'bg-lochmara-blue',
              },
              {
                step: '02',
                title: 'Trust Built Over Time',
                text: 'What began as a response to a single, preventable tragedy has grown into a trusted institution — one that continues to heal, empower, and restore hope.',
                accent: 'bg-brand',
              },
              {
                step: '03',
                title: 'Our Mission Forward',
                text: 'As we look to the future, our mission remains clear: to deliver compassionate, evidence-based healthcare; to strengthen partnerships that expand our reach; and to honor the lives that inspired our journey by ensuring that fewer families endure the pain of preventable loss.',
                accent: 'bg-malachite-green',
              },
            ].map((item) => (
              <div
                key={item.step}
                className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-gradient-to-r from-white to-blue-50/30 p-4 transition hover:shadow-md sm:flex-row sm:gap-5 sm:p-5 md:gap-6 md:p-6"
              >
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center self-start rounded-xl ${item.accent} font-ubuntu text-xs font-bold text-white shadow-md sm:h-12 sm:w-12 sm:text-sm`}
                >
                  {item.step}
                </span>
                <div className="min-w-0">
                  <h3 className="mb-1 font-ubuntu text-base font-bold text-lochmara-blue sm:mb-2 sm:text-lg md:text-xl">
                    {item.title}
                  </h3>
                  <p className="font-redhat text-sm leading-relaxed text-gray-700 sm:text-base md:text-lg">{item.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-100 bg-gradient-to-r from-lochmara-blue/5 to-malachite-green/5 px-4 py-4 text-center sm:px-6 sm:py-6 md:px-10">
            <p className="font-redhat text-sm italic text-congress-blue sm:text-base md:text-lg">
              Ensuring that fewer families endure the pain of preventable loss.
            </p>
          </div>
        </article>
      </div>
    </section>

    {/* CTA */}
    <section className="flex w-full flex-col items-center px-4 py-10 sm:py-12">
      <h2 className="mb-3 px-2 text-center font-ubuntu text-xl font-bold text-lochmara-blue sm:mb-4 sm:text-2xl md:text-3xl">
        Need Care or Want to Partner With Us?
      </h2>
      <p className="mb-6 max-w-2xl px-2 text-center font-redhat text-base text-gray-700 sm:mb-8 sm:text-lg">
        Book an appointment, speak with our team, or explore how PureMedify and The Great Emmanuel Hospital
        can serve your community.
      </p>
      <div className="flex w-full max-w-md flex-col gap-3 sm:max-w-none sm:flex-row sm:flex-wrap sm:justify-center sm:gap-4">
        <Link
          to="/contact"
          className="w-full rounded-full bg-brand px-6 py-3 text-center font-ubuntu text-base font-semibold text-white shadow-lg transition hover:scale-105 hover:bg-brand-green sm:w-auto sm:px-8 sm:text-lg"
        >
          Book an Appointment
        </Link>
        <Link
          to="/services"
          className="w-full rounded-full border-2 border-lochmara-blue px-6 py-3 text-center font-ubuntu text-base font-semibold text-lochmara-blue transition hover:border-malachite-green hover:text-malachite-green sm:w-auto sm:px-8 sm:text-lg"
        >
          View All Services
        </Link>
      </div>
    </section>
  </div>
);

export default GreatEmmanuelHospital;
